import { useEffect, useRef, useState } from 'react';
import { IconPlus, IconTag, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { api, type TagEntry, UnauthorizedError } from '../api.js';
import { colorForTag } from './tagColor.js';

const TAG_RE = /^[a-z0-9][a-z0-9_.-]{0,63}$/;

interface TagChipsProps {
  depotPath: string;
}

type Tone = 'neutral' | 'orange' | 'green' | 'red' | 'blue' | 'yellow';
function toneFromTagColor(name: string): Tone {
  switch (colorForTag(name)) {
    case 'green': return 'green';
    case 'yellow': return 'yellow';
    case 'red': return 'red';
    case 'blue':
    case 'cyan':
    case 'teal':
    case 'indigo':
      return 'blue';
    case 'orange':
    case 'pink':
    case 'grape':
    case 'violet':
    case 'lime':
      return 'orange';
    default: return 'neutral';
  }
}

/**
 * Tag-chip strip rendered under the file metadata block.
 *
 * Read: `/api/depot/tags` → tinted Badge chips with attribution
 * tooltips and × remove buttons. Add: `+` button opens a popover
 * with an Input + suggestion list from `/api/depot/tags/suggest`.
 *
 * Accessibility: a visually-hidden aria-live region announces every
 * mutation so screen-reader users hear "Added tag X" / "Removed
 * tag X".
 */
export function TagChips({ depotPath }: TagChipsProps) {
  const qc = useQueryClient();
  const queryKey = ['depot.tags', depotPath];

  const [liveMsg, setLiveMsg] = useState('');
  const announce = useRef((_msg: string) => {});
  useEffect(() => {
    announce.current = (msg: string) => {
      setLiveMsg((prev) => (prev === msg ? msg + '​' : msg));
    };
  }, []);

  const tagsQuery = useQuery({
    queryKey,
    queryFn: () => api.tagsForPath(depotPath),
  });

  const addMutation = useMutation({
    mutationFn: (tag: string) => api.addTag(depotPath, tag),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey });
      announce.current(`Added tag ${res.tag}`);
    },
    onError: (err: Error) => {
      toast.error('Failed to add tag', { description: err.message });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (tag: string) => api.removeTag(depotPath, tag),
    onSuccess: (_, tag) => {
      qc.invalidateQueries({ queryKey });
      announce.current(`Removed tag ${tag}`);
    },
  });

  if (tagsQuery.isLoading) return null;
  if (tagsQuery.error instanceof UnauthorizedError) return null;

  const tags = tagsQuery.data?.tags ?? [];

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <IconTag size={12} className="text-muted-foreground" />
      {tags.map((t) => (
        <TagPill
          key={t.tag}
          entry={t}
          onRemove={() => removeMutation.mutate(t.tag)}
          isRemoving={
            removeMutation.isPending && removeMutation.variables === t.tag
          }
        />
      ))}
      <AddTagPopover
        onAdd={(tag) => addMutation.mutate(tag)}
        isAdding={addMutation.isPending}
        existingTags={tags.map((t) => t.tag.toLowerCase())}
      />
      <span
        aria-live="polite"
        aria-atomic="true"
        className="absolute h-px w-px overflow-hidden whitespace-nowrap p-0"
        style={{ clip: 'rect(0,0,0,0)' }}
      >
        {liveMsg}
      </span>
    </div>
  );
}

function TagPill({
  entry,
  onRemove,
  isRemoving,
}: {
  entry: TagEntry;
  onRemove: () => void;
  isRemoving: boolean;
}) {
  const tooltip = `Added by ${entry.createdBy} on ${new Date(entry.createdAt).toLocaleDateString()}`;
  return (
    <Tooltip label={tooltip}>
      <span className="inline-flex items-center">
        <Badge tone={toneFromTagColor(entry.tag)} className="gap-1 pr-0.5">
          {entry.tag}
          <button
            type="button"
            onClick={onRemove}
            disabled={isRemoving}
            aria-label={`Remove tag ${entry.tag}`}
            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm transition-colors hover:bg-black/30 disabled:opacity-40"
          >
            <IconX size={10} stroke={2.5} />
          </button>
        </Badge>
      </span>
    </Tooltip>
  );
}

function AddTagPopover({
  onAdd,
  isAdding,
  existingTags,
}: {
  onAdd: (tag: string) => void;
  isAdding: boolean;
  existingTags: string[];
}) {
  const [opened, setOpened] = useState(false);
  const [value, setValue] = useState('');
  const trimmed = value.trim();

  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(trimmed), 150);
    return () => window.clearTimeout(id);
  }, [trimmed]);

  const suggestQuery = useQuery({
    queryKey: ['tags.suggest', debounced],
    queryFn: () => api.tagSuggestions(debounced, 10),
    enabled: opened,
  });

  const suggestions = (suggestQuery.data?.suggestions ?? [])
    .filter((s) => !existingTags.includes(s.tag.toLowerCase()))
    .map((s) => s.tag);

  const isCaseDupe = existingTags.includes(trimmed.toLowerCase());
  const isShapeValid = TAG_RE.test(trimmed);

  function submit() {
    if (!isShapeValid || isCaseDupe) return;
    onAdd(trimmed);
    setValue('');
    setOpened(false);
  }

  return (
    <Popover open={opened} onOpenChange={setOpened}>
      <PopoverTrigger asChild>
        <Tooltip label="Add tag">
          <Button variant="ghost" size="icon" aria-label="Add tag">
            <IconPlus />
          </Button>
        </Tooltip>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <div className="flex flex-col gap-2">
          <p className="text-[11px] text-muted-foreground">
            Pick an existing tag or type a new one. Lowercase letters,
            digits, dot, dash, underscore. ≤64 chars.
          </p>
          <Input
            placeholder="e.g. approved"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value.toLowerCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
              if (e.key === 'Escape') setOpened(false);
            }}
            autoFocus
          />
          {trimmed.length > 0 && isCaseDupe && (
            <p className="text-[11px] text-red-400">Already tagged</p>
          )}
          {trimmed.length > 0 && !isCaseDupe && !isShapeValid && (
            <p className="text-[11px] text-red-400">Invalid tag shape</p>
          )}
          {suggestions.length > 0 && (
            <div className="flex flex-col">
              <span className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Suggestions
              </span>
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    onAdd(s);
                    setOpened(false);
                    setValue('');
                  }}
                  className="flex items-center rounded-sm px-2 py-1 text-left text-xs transition-colors hover:bg-neutral-800"
                >
                  <Badge tone={toneFromTagColor(s)}>{s}</Badge>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">
              {isAdding ? 'Adding…' : ' '}
            </span>
            <button
              type="button"
              onClick={submit}
              disabled={!isShapeValid || isCaseDupe}
              className={cn(
                'text-[11px]',
                isShapeValid && !isCaseDupe
                  ? 'cursor-pointer text-theia-400 hover:underline'
                  : 'cursor-default text-muted-foreground',
              )}
              aria-label="Add tag"
            >
              Enter ↵
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
